import * as yup from 'yup';
import { treasuryKybFormFields } from '../../fields/formFields/treasuryKyb';

/** Alpaca's own value style: lower snake case, e.g. limited_liability_corporation (the backend's rule). */
const ALPACA_VALUE = /^[a-z][a-z0-9_]*$/;
const ALPACA_VALUE_MESSAGE = 'Use lower snake case, e.g. limited_liability_corporation';

/** ISO 3166-1 alpha-3, upper case. */
const ALPHA_3 = /^[A-Z]{3}$/;

const amount = (label: string) =>
  yup
    .number()
    .typeError(`${label} must be a number`)
    .min(0, `${label} must be 0 or more`)
    .required(`${label} is required`);

/** A range's max must be at least its min. */
const maxOf = (label: string, minField: treasuryKybFormFields) =>
  amount(label).test('max-after-min', `${label} must be at least the minimum`, function checkMax(value) {
    const min = Number(this.parent[minField]);

    return value == null || Number.isNaN(min) || value >= min;
  });

export const treasuryKybSchema = yup.object({
  [treasuryKybFormFields.legalEntityType]: yup
    .string()
    .matches(ALPACA_VALUE, ALPACA_VALUE_MESSAGE)
    .max(64)
    .required('Legal entity type is required'),
  [treasuryKybFormFields.dateOfIncorporation]: yup.string().required('Date of incorporation is required'),
  [treasuryKybFormFields.industry]: yup
    .string()
    .matches(ALPACA_VALUE, ALPACA_VALUE_MESSAGE)
    .max(64)
    .required('Industry is required'),
  [treasuryKybFormFields.fundingSource]: yup
    .array()
    .of(yup.string().matches(ALPACA_VALUE, ALPACA_VALUE_MESSAGE).max(64))
    .min(1, 'Add at least one funding source')
    .max(10, 'At most 10 funding sources')
    .required('Add at least one funding source'),
  [treasuryKybFormFields.countryOfIncorporation]: yup.string().matches(ALPHA_3).required('Country is required'),
  [treasuryKybFormFields.countryOfTaxResidence]: yup.string().matches(ALPHA_3).required('Country is required'),
  [treasuryKybFormFields.annualIncomeMin]: amount('Annual income min'),
  [treasuryKybFormFields.annualIncomeMax]: maxOf('Annual income max', treasuryKybFormFields.annualIncomeMin),
  [treasuryKybFormFields.liquidNetWorthMin]: amount('Liquid net worth min'),
  [treasuryKybFormFields.liquidNetWorthMax]: maxOf('Liquid net worth max', treasuryKybFormFields.liquidNetWorthMin),
  [treasuryKybFormFields.totalNetWorthMin]: amount('Total net worth min'),
  [treasuryKybFormFields.totalNetWorthMax]: maxOf('Total net worth max', treasuryKybFormFields.totalNetWorthMin),
  [treasuryKybFormFields.isParticipatingFatca]: yup.boolean().required(),
  [treasuryKybFormFields.isExemptFromTaxUnder501a]: yup.boolean().required(),
  [treasuryKybFormFields.ownerPartyEmail]: yup
    .string()
    .email('Enter a valid email')
    .max(255)
    .required('Owner party email is required'),
  [treasuryKybFormFields.entityPartyEmail]: yup
    .string()
    .email('Enter a valid email')
    .max(255)
    .required('Company party email is required')
    .notOneOf([yup.ref(treasuryKybFormFields.ownerPartyEmail)], 'Must differ from the owner party email'),
  [treasuryKybFormFields.titleAtCompany]: yup.string().trim().max(100).required('Title at company is required'),
  [treasuryKybFormFields.countryOfBirth]: yup.string().matches(ALPHA_3).required('Country of birth is required'),
});
